import { devtoolsExchange } from "@urql/devtools";
import { createClient as createWSClient } from "graphql-ws";
import { type FC, type ReactNode, useEffect } from "react";
import {
  Client,
  type CombinedError,
  type Exchange,
  fetchExchange,
  mapExchange,
  type Operation,
  Provider,
  subscriptionExchange,
} from "urql";
import { pipe, tap } from "wonka";
import { toast } from "@/components/ui/toast";
import { standardAuthFetch } from "@/data-access/core/authenticatedFetch";
import {
  dataServiceBaseUrl,
  wsServiceBaseUrl,
} from "@/data-access/core/constants";
import { getAuthToken } from "@/data-access/core/tokenManagement";
import {
  ArchiveBriefingTemplate,
  PublishBriefingTemplate,
} from "@/data-access/scheduled-briefings/queries/template-layout-queries";
import {
  ExchangeSso,
  Login,
  StartSso,
} from "@/data-access/user/queries/auth-queries";
import { createCacheConfig } from "@/lib/urql/cache-config";

const getOperationName = (query: Operation["query"]): string | undefined => {
  const firstOpDef = query.definitions[0];
  return firstOpDef?.kind === "OperationDefinition"
    ? firstOpDef.name?.value
    : undefined;
};

const SELF_TOASTING_OPERATION_NAMES = new Set(
  [
    Login,
    StartSso,
    ExchangeSso,
    ArchiveBriefingTemplate,
    PublishBriefingTemplate,
  ]
    .map(getOperationName)
    .filter((name): name is string => Boolean(name)),
);

/**
 * True for mutations that already toast their own failure (auth through
 * `handleAuthError`, template archive and unarchive in the Templates page),
 * so the global error toast would duplicate it.
 */
export const isSelfToastingOperation = (query: Operation["query"]): boolean => {
  const operationName = getOperationName(query);
  return Boolean(
    operationName && SELF_TOASTING_OPERATION_NAMES.has(operationName),
  );
};

const DEGENERATE_NETWORK_ERROR_LOG_PATTERN =
  /^\[Network\]\s*(?:undefined|null)?$/;

/**
 * Console message for a URQL error, or `null` when it carries no actionable
 * text: an empty `CombinedError` (bare minified stack) or a message-less
 * network failure that URQL renders as `[Network] undefined`.
 */
export const resolveUrqlErrorLogMessage = (
  error: CombinedError,
): string | null => {
  const message = error.message;
  if (!message || DEGENERATE_NETWORK_ERROR_LOG_PATTERN.test(message)) {
    return null;
  }
  return message;
};

const EXPECTED_WS_CLOSE_CODES = new Set([1000, 1001, 4499]);
const RECOVERABLE_WS_TRANSPORT_CLOSE_CODES = new Set([1005, 1006]);
const GRAPHQL_WS_PROTOCOL_CLOSE_CODES = new Set([4400, 4401, 4403, 4408, 4429]);

interface WebSocketCloseEventLike {
  code?: number;
  name?: string;
}

interface WebSocketTransportEventLike extends WebSocketCloseEventLike {
  currentTarget?: unknown;
  target?: unknown;
  type?: string;
}

const isDocumentHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

const isWebSocketTarget = (target: unknown): boolean => {
  if (!target) {
    return false;
  }

  if (typeof WebSocket !== "undefined" && target instanceof WebSocket) {
    return true;
  }

  if (typeof target === "string") {
    return target === "[WebSocket]";
  }

  if (typeof target !== "object") {
    return false;
  }

  return (
    (target as { constructor?: { name?: string } }).constructor?.name ===
    "WebSocket"
  );
};

/**
 * Detects native WebSocket transport events that graphql-ws can reconnect from.
 */
export const isRecoverableWebSocketTransportEvent = (
  event: unknown,
): boolean => {
  const transportEvent = event as WebSocketTransportEventLike;

  if (typeof transportEvent.code === "number") {
    if (GRAPHQL_WS_PROTOCOL_CLOSE_CODES.has(transportEvent.code)) {
      return false;
    }
    return RECOVERABLE_WS_TRANSPORT_CLOSE_CODES.has(transportEvent.code);
  }

  if (transportEvent.type !== "error" && transportEvent.type !== "close") {
    return false;
  }

  return (
    isWebSocketTarget(transportEvent.target) ||
    isWebSocketTarget(transportEvent.currentTarget)
  );
};

/**
 * Classifies WebSocket closes that the client can recover from without user-visible failure.
 */
export const isExpectedWebSocketClose = (event: unknown): boolean => {
  const closeEvent = event as WebSocketCloseEventLike;

  if (closeEvent.name === "TerminatedCloseEvent") {
    return true;
  }

  if (typeof closeEvent.code !== "number") {
    return isDocumentHidden() || isRecoverableWebSocketTransportEvent(event);
  }

  return (
    EXPECTED_WS_CLOSE_CODES.has(closeEvent.code) ||
    isRecoverableWebSocketTransportEvent(event)
  );
};

// Create a custom exchange to track operations by their ID
const requestIdExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      tap((operation: Operation) => {
        // Only for queries and mutations
        if (operation.kind === "query" || operation.kind === "mutation") {
          const opName = getOperationName(operation.query) ?? "unknown";

          // Get feedId if it exists
          const feedId = operation.variables?.feedId || "none";

          // Create consistent request ID
          const requestId = `${opName}:${feedId}`;

          // Store the request ID in the operation context
          operation.context = {
            ...operation.context,
            requestId,
          };
        }
      }),
      forward,
    );
  };

// Instead of adding a custom header, we'll add the request ID to the query variables
const requestIdVariableExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      tap((operation: Operation) => {
        if (operation.kind === "query" || operation.kind === "mutation") {
          // Get the request ID from the context
          const requestId = operation.context.requestId;

          if (requestId) {
            // Add the request ID to the query variables
            operation.variables = {
              ...operation.variables,
              // Use a variable name that won't conflict with existing variables
              __requestId: requestId,
            };
          }
        }
      }),
      forward,
    );
  };

//https://the-guild.dev/graphql/ws/recipes#ws-server-and-client-auth-usage-with-token-expiration-validation-and-refresh
const wsClient = createWSClient({
  url: `${wsServiceBaseUrl}/graphql/`,
  connectionParams: async () => {
    try {
      const accessToken = await getAuthToken();

      if (!accessToken) {
        return {};
      }

      return {
        headers: {
          token: `Bearer ${accessToken}`,
        },
      };
    } catch (err) {
      console.error("Error getting WS connection params:", err);
      return {};
    }
  },
  on: {
    closed: (event: unknown) => {
      if (isExpectedWebSocketClose(event)) {
        console.debug("WebSocket connection closed:", event);
      } else {
        console.error("WebSocket connection closed unexpectedly:", event);
      }
    },
    connected: (socket: unknown) => {
      console.info("WebSocket client connected.", socket);
    },
    error: (err) => {
      if (isRecoverableWebSocketTransportEvent(err)) {
        console.debug("WebSocket connection error:", err);
      } else {
        console.error("WebSocket connection error:", err);
      }
    },
  },
});

// Create a function to create a new URQL client
const createUrqlClient = () => {
  return new Client({
    url: `${dataServiceBaseUrl}/graphql/`,
    exchanges: [
      requestIdExchange,
      requestIdVariableExchange,
      devtoolsExchange,
      createCacheConfig(),
      mapExchange({
        onError(error, operation) {
          if (isSelfToastingOperation(operation.query)) {
            return;
          }

          const networkError = error.networkError;
          if (
            networkError instanceof Error &&
            (networkError.name === "AbortError" ||
              networkError.message.toLowerCase().includes("abort"))
          ) {
            return;
          }

          const errorMessages = error.graphQLErrors
            .map((graphQLError) => graphQLError.message)
            .filter((message) => message.length > 0);
          if (networkError?.message) {
            errorMessages.push(networkError.message);
          }
          if (errorMessages.length > 0) {
            toast.error(errorMessages);
          }

          const logMessage = resolveUrqlErrorLogMessage(error);
          if (logMessage) {
            console.error(logMessage);
          }
        },
      }),
      fetchExchange,
      subscriptionExchange({
        forwardSubscription(request) {
          const input = { ...request, query: request.query || "" };
          return {
            subscribe(sink) {
              try {
                // Wrap the sink to catch any errors from the WebSocket
                const wrappedSink = {
                  next: (value: Parameters<typeof sink.next>[0]) => {
                    try {
                      sink.next(value);
                    } catch (error) {
                      console.error("Error in subscription sink.next:", error);
                      sink.error(error as Error);
                    }
                  },
                  error: (error: unknown) => {
                    if (isRecoverableWebSocketTransportEvent(error)) {
                      console.debug("Subscription error:", error);
                    } else {
                      console.error("Subscription error:", error);
                    }
                    sink.error(error as Error);
                  },
                  complete: () => {
                    sink.complete();
                  },
                };

                const unsubscribe = wsClient.subscribe(input, wrappedSink);
                return { unsubscribe };
              } catch (error) {
                // Handle synchronous subscription setup errors
                console.error("Error setting up subscription:", error);
                sink.error(error as Error);
                return {
                  unsubscribe: () => {
                    // No-op if subscription failed
                  },
                };
              }
            },
          };
        },
      }),
    ],
    // Use the original standardAuthFetch without modifications
    fetch: standardAuthFetch,
  });
};

// Create the initial URQL client
export let client = createUrqlClient();

// Add reset capability to clear URQL cache and state
export const resetClient = async () => {
  try {
    // Close websocket connection
    wsClient.terminate();

    client = createUrqlClient();
    return true;
  } catch (error) {
    console.error("Error resetting URQL client:", error);
    return false;
  }
};

/**
 * When the tab becomes visible again after being idle or backgrounded, force the
 * WebSocket to reconnect so connectionParams runs with a fresh token. Prevents
 * "WebSocket closed" and empty-headers/CORS errors after tab focus or refresh.
 */
const useVisibilityReconnect = () => {
  useEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | undefined;
    const handler = () => {
      if (document.visibilityState !== "visible") return;
      if (debounceId !== undefined) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        debounceId = undefined;
        wsClient.terminate();
      }, 400);
    };
    document.addEventListener("visibilitychange", handler);
    return () => {
      document.removeEventListener("visibilitychange", handler);
      if (debounceId !== undefined) clearTimeout(debounceId);
    };
  }, []);
};

const UrqlProvider: FC<{ children: ReactNode }> = ({ children }) => {
  useVisibilityReconnect();
  return <Provider value={client}>{children}</Provider>;
};

export default UrqlProvider;
